import axios from 'axios';
import setAuthToken from './setAuthToken';
import { LSGO_AUTHENTICATION_BASE_URL } from 'config/lsgoAuthentication';
import { safeReturnPath, startLsgoAuthenticationLogin } from './lsgoAuthenticationRedirectFlow';
import { isFramed } from './lsgoAuthEmbedSession';

/**
 * Schools through lsgo-authentication, for sessions signed in there.
 *
 * Today the school switcher still asks legacy first
 * (POST /api/v4/auth/login/audience/:schoolId), which answers with `next`
 * when auth should handle it (lsgo service/lsgoAuthSchoolMode.js);
 * followSchoolSwitch does what it says. Once every school is on auth, the
 * app calls these directly: listLsgoAuthenticationSchools for the list and
 * switchLsgoAuthenticationSchool to change school.
 */

/** The account's schools from auth: [{ tenant, roles, displayName }]. Needs the current access token. */
export async function listLsgoAuthenticationSchools() {
    const { data } = await axios.get(`${LSGO_AUTHENTICATION_BASE_URL}/auth/memberships`, {
        headers: { Authorization: axios.defaults.headers.common.Authorization },
    });
    return data?.tenants ?? [];
}

/**
 * Change school on the auth session (the HttpOnly refresh cookie): auth
 * checks the membership, rotates the session to that school and returns its
 * access token. A 403 ({ error: 'tenant_unavailable', reason }) leaves the
 * current school as it was.
 */
export async function switchLsgoAuthenticationSchool(tenant) {
    const { data } = await axios.post(
        `${LSGO_AUTHENTICATION_BASE_URL}/token/switch-tenant`,
        { tenant },
        { withCredentials: true }
    );
    setAuthToken(data.access_token);
    return data;
}

/**
 * Act on legacy's answer to a school switch. Returns true when auth handled
 * it (the caller reloads as before), 'redirect' when the page is leaving to
 * sign in, false for a legacy switch. `returnPath` (default: this page) is
 * where the sign-in comes back to.
 */
export async function followSchoolSwitch(answer, { returnPath } = {}) {
    if (answer?.next === 'lsgo_authentication_switch') {
        await switchLsgoAuthenticationSchool(answer.tenant);
        return true;
    }
    if (answer?.next === 'lsgo_authentication_sign_in') {
        await startLsgoAuthenticationLogin(returnPath ?? window.location.pathname + window.location.search, {
            tenant: answer.tenant,
        });
        return 'redirect';
    }
    return false;
}

/**
 * Legacy refuses its own sign-in (password, Google, SIS SSO return, session
 * refresh) for a user whose school is on lsgo-authentication: HTTP 409
 * { code: 'AUTH_MANAGED', next: 'lsgo_authentication_sign_in', tenant }.
 * config/axios (the shared lsgo-fe client) resolves that failure as the
 * response itself, because its body has a `code`; a plain axios rejection
 * carries it on error.response. Takes either and returns the body, or null.
 */
export function authManagedAnswer(resultOrError) {
    const res = resultOrError?.response ?? resultOrError;
    const data = res?.data;
    if (Number(res?.status) !== 409 || data?.code !== 'AUTH_MANAGED') return null;
    if (data.next !== 'lsgo_authentication_sign_in' || typeof data.tenant !== 'string' || !data.tenant) return null;
    return data;
}

/**
 * Where a refused legacy sign-in should land after signing in through auth:
 * the page's ?redirectUri= (sign-in links), else this page, except the
 * sign-in pages themselves, which go to /teacher.
 */
function signInReturnPath() {
    const { pathname, search } = window.location;
    const redirectUri = new URLSearchParams(search).get('redirectUri');
    if (redirectUri) return safeReturnPath(redirectUri);
    if (pathname === '/' || pathname.startsWith('/tfa')) return '/teacher';
    return pathname + search;
}

/**
 * Sign in through auth for an AUTH_MANAGED answer (same as followSchoolSwitch's
 * sign-in) and resolve true: the page is leaving. Resolves false (the
 * caller's usual failure stands) inside an SIS iframe, where auth's pages
 * can't be framed and SIS schools on auth already sign in through the iframe
 * flow (utils/sisSchoolAuth.js), and on this app's own /auth/* pages
 * (/auth/start, /auth/callback), which are already signing in.
 */
export async function startAuthManagedSignIn(answer, { returnPath } = {}) {
    if (isFramed() || window.location.pathname.startsWith('/auth/')) return false;
    await followSchoolSwitch(answer, { returnPath: returnPath ?? signInReturnPath() });
    return true;
}

/**
 * If legacy answered AUTH_MANAGED, start lsgo-authentication sign-in for that
 * school and resolve true: the page is leaving, the caller stops. Otherwise
 * resolve false and change nothing.
 */
export async function signInThroughAuthIfManaged(resultOrError, options) {
    const answer = authManagedAnswer(resultOrError);
    if (!answer) return false;
    return startAuthManagedSignIn(answer, options);
}
